import React, { useEffect, useRef, useState, useCallback } from "react";
import datafeed from "./datafeed";
import { fetchPSXSymbols } from "./helpers";

const TradingViewChart = () => {
  const chartRef    = useRef(null);
  const widgetRef   = useRef(null);

  const [symbols,        setSymbols]        = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState(null); // { code, name }
  const [query,          setQuery]          = useState("");
  const [showDropdown,   setShowDropdown]   = useState(false);
  const [loadingSymbols, setLoadingSymbols] = useState(true);
  const dropdownRef = useRef();

  // ── fetch PSX symbol list once ───────────────────────────────────────────
  useEffect(() => {
    fetchPSXSymbols()
      .then(list => {
        setSymbols(list);
        if (list.length > 0) setSelectedSymbol(list[0]);
      })
      .catch(() => {})
      .finally(() => setLoadingSymbols(false));
  }, []);

  // ── mount / re-mount TradingView widget when symbol changes ──────────────
  useEffect(() => {
    if (!selectedSymbol) return;
    if (!window.TradingView) {
      console.error("TradingView charting library not loaded");
      return;
    }

    // destroy previous widget
    if (widgetRef.current) {
      try { widgetRef.current.remove(); } catch (_) {}
      widgetRef.current = null;
    }

    widgetRef.current = new window.TradingView.widget({
      container:    chartRef.current,
      locale:       "en",
      library_path: "/charting_library/",
      datafeed,
      symbol:       `PSX:${selectedSymbol.code}`,
      interval:     "1D",
      fullscreen:   false,
      autosize:     true,
      timezone:     "Asia/Karachi",
      theme:        "light",
      debug:        false,
      // allow built-in symbol search (uses datafeed.searchSymbols)
      disabled_features: [],
      enabled_features:  ["hide_left_toolbar_by_default"],
    });

    return () => {
      if (widgetRef.current) {
        try { widgetRef.current.remove(); } catch (_) {}
        widgetRef.current = null;
      }
    };
  }, [selectedSymbol]);

  // ── close dropdown on outside click ─────────────────────────────────────
  useEffect(() => {
    function handle(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setShowDropdown(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const filtered = query.trim()
    ? symbols.filter(s =>
        s.code?.toLowerCase().includes(query.toLowerCase()) ||
        s.name?.toLowerCase().includes(query.toLowerCase())
      )
    : symbols;

  const pickSymbol = useCallback((sym) => {
    setSelectedSymbol(sym);
    setQuery("");
    setShowDropdown(false);
  }, []);

  // ── styles ───────────────────────────────────────────────────────────────
  const toolbarStyle = {
    display: "flex", alignItems: "center", gap: 12,
    padding: "10px 16px", background: "#fff",
    borderBottom: "1px solid #e5e7eb", flexShrink: 0,
  };
  const inputWrapStyle = {
    display: "flex", alignItems: "center",
    border: "1px solid #e5e7eb", borderRadius: 8,
    overflow: "hidden", background: "#fff",
  };
  const dropdownStyle = {
    position: "absolute", top: "calc(100% + 4px)", right: 0,
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
    boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
    maxHeight: 280, overflowY: "auto", zIndex: 9999, minWidth: 230,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 120px)", minHeight: 540 }}>

      {/* ── toolbar ── */}
      <div style={toolbarStyle}>
        <span style={{ fontWeight: 700, color: "#050a30", fontSize: 15 }}>
          Real-Time Trading Chart
        </span>

        {selectedSymbol && (
          <span style={{
            background: "#f98300", color: "#fff",
            padding: "3px 12px", borderRadius: 6,
            fontFamily: "monospace", fontWeight: 700, fontSize: 14,
          }}>
            PSX: {selectedSymbol.code}
          </span>
        )}
        {selectedSymbol?.name && (
          <span style={{ color: "#888", fontSize: 13 }}>{selectedSymbol.name}</span>
        )}

        {/* symbol search */}
        <div ref={dropdownRef} style={{ position: "relative", marginLeft: "auto" }}>
          <div style={inputWrapStyle}>
            <span style={{ padding: "0 10px", color: "#bbb", fontSize: 15 }}>🔍</span>
            <input
              type="text"
              placeholder={loadingSymbols ? "Loading symbols…" : "Search symbol or name…"}
              value={query}
              disabled={loadingSymbols}
              onChange={e => { setQuery(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              style={{
                border: "none", outline: "none",
                padding: "8px 10px 8px 0", fontSize: 13,
                width: 200, fontFamily: "inherit", background: "transparent",
              }}
            />
          </div>

          {showDropdown && filtered.length > 0 && (
            <div style={dropdownStyle}>
              {filtered.slice(0, 80).map(sym => (
                <div
                  key={sym.code}
                  onMouseDown={() => pickSymbol(sym)}
                  style={{
                    padding: "8px 14px", cursor: "pointer", fontSize: 13,
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    borderBottom: "1px solid #f5f5f5",
                    background: selectedSymbol?.code === sym.code ? "#fff7ed" : "transparent",
                  }}
                  onMouseEnter={e  => e.currentTarget.style.background = "#f9fafb"}
                  onMouseLeave={e  => e.currentTarget.style.background = selectedSymbol?.code === sym.code ? "#fff7ed" : "transparent"}
                >
                  <span style={{ fontWeight: 700, color: "#050a30", fontFamily: "monospace" }}>
                    {sym.code}
                  </span>
                  <span style={{
                    color: "#888", fontSize: 11, marginLeft: 8,
                    maxWidth: 140, overflow: "hidden",
                    textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {sym.name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── chart ── */}
      <div ref={chartRef} style={{ flex: 1, minHeight: 0, width: "100%" }} />
    </div>
  );
};

export default TradingViewChart;
